/**
 * Resolving the consent gate — the reason nothing is being collected today.
 *
 * CONFIDENTIAL — ASI Internal.
 * Copyright 2026, Partnership Net; Agency for Sponsorship Intelligence, Lda;
 * Advanced Sponsorship Insights, GmbH. Confidential and proprietary.
 *
 * `emit.ts` starts with the gate shut and exposes `setTelemetryConsent` to open it.
 * Nothing in the application ever called it, so every `signal()` in this codebase has
 * been returning early since the day it was written: the call sites are correct, the
 * registry is correct, and the sink has always been empty. This module is the missing
 * half — it decides, once, whether the gate opens.
 *
 * THREE MODES, because "is telemetry on?" has three different answers and collapsing
 * them is how a client-facing rule gets weakened by accident.
 *
 *   off       Nothing is recorded. The default, and the only safe default.
 *
 *   internal  ASI staff, on this product's illustrative data, with no client tenant in
 *             the picture. ASI is controller of its own people's usage of its own
 *             internal tool, for the purpose of building the thing — a legitimate
 *             interest that stands on its own and needs no client contract, because no
 *             client's data and no client's users are involved. This is the mode that
 *             lets collection start today.
 *
 *   tenant    A real client's users. Requires the signed service-usage controller
 *             clause, and that fact cannot come from a build-time environment variable
 *             — a per-tenant flag is a per-tenant thing to get wrong. It arrives from
 *             the arena access layer (DEC-13). Until then this mode resolves to OFF and
 *             says why, rather than quietly behaving like `internal`.
 *
 * WHY THIS STAYS MEMORY-ONLY. Opening the gate does not open a storage surface. No
 * cookie, no localStorage, no id that survives a refresh. Writing an identifier to a
 * visitor's device is terminal-equipment access under TDDDG section 25 and the
 * Portuguese equivalent, and it needs prior consent whether or not the thing stored is
 * personal data. Staying in memory keeps that question off the table entirely, and at
 * this stage costs almost nothing: the unit of analysis is a session walking a flow,
 * and a session is exactly what a memory sink holds.
 */

import { setTelemetryConsent } from './emit';

export type TelemetryMode = 'off' | 'internal' | 'tenant';

export interface TelemetryResolution {
  readonly mode: TelemetryMode;
  readonly enabled: boolean;
  /** Why the gate is in the state it is, in words a non-engineer can act on. */
  readonly because: string;
}

const MODES: readonly TelemetryMode[] = ['off', 'internal', 'tenant'];

const isMode = (value: unknown): value is TelemetryMode =>
  typeof value === 'string' && (MODES as readonly string[]).includes(value);

/**
 * Read the configured mode.
 *
 * Unset means off. An unrecognised value also means off, loudly: a typo in an env file
 * should not silently disable collection for a month, and it should not silently enable
 * it either.
 */
export function readTelemetryMode(raw: unknown): TelemetryMode {
  if (raw === undefined || raw === null || raw === '') return 'off';
  if (isMode(raw)) return raw;
  // `raw` is unknown and may not be a string at all, so it is described rather than
  // stringified: `String({})` prints "[object Object]", which tells a reader nothing.
  const shown = typeof raw === 'string' ? `"${raw}"` : `a ${typeof raw}`;
  console.warn(
    `[telemetry] VITE_TELEMETRY_MODE is ${shown}, not one of ${MODES.join(' | ')} — treating as "off".`,
  );
  return 'off';
}

/** Decide the gate from a mode. Pure, so the decision is testable without a DOM. */
export function resolveTelemetry(mode: TelemetryMode): TelemetryResolution {
  switch (mode) {
    case 'internal':
      return {
        mode,
        enabled: true,
        because:
          'Internal mode: ASI staff on illustrative data, no client tenant. Legitimate interest in improving our own product. Memory-only, nothing written to the device, nothing leaves the browser.',
      };
    case 'tenant':
      return {
        mode,
        enabled: false,
        because:
          'Tenant mode is not available from configuration. A real client tenant needs the signed service-usage controller clause, resolved per tenant by the arena access layer (DEC-13). Gate stays shut.',
      };
    case 'off':
    default:
      return { mode: 'off', enabled: false, because: 'Telemetry is off. Nothing is recorded.' };
  }
}

/**
 * Apply the resolution to the emit client. Call once, at startup.
 *
 * The `tenantClause` and `userConsent` pair in `emit.ts` describes the CLIENT case and is
 * left exactly as it is: both must be true for anything to record. Internal mode sets
 * both because in that mode both are genuinely satisfied — there is no client tenant
 * whose clause could be missing, and the people being measured are the ones building the
 * product. It is not a bypass; it is a different situation with its own honest answer.
 */
export function initTelemetry(raw: unknown): TelemetryResolution {
  const resolution = resolveTelemetry(readTelemetryMode(raw));
  setTelemetryConsent({
    tenantClause: resolution.enabled,
    userConsent: resolution.enabled,
  });
  return resolution;
}
